import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Header,
  Button,
  Card,
  Text,
  Space,
  CarInfo,
  SelectCarPop,
  MileageConfirmPopup,
} from "../../Components/Index";
import { useCars } from "../../Context/CarContext";
import { createService, getCategoryById } from "../../services/api/serviceApi";
import { extractErrorMessage } from "../../services/api/errorHandler";
import { buildDetailsWithNextServiceMileage } from "../../services/nextServiceMileage";
// Reusable mileage logic: Current + 10,000 default, below-current checks,
// entered-vs-vehicle comparison. Pure + shared (see src/services/mileage.js).
import {
  formatDefaultNextMileage,
  isNextMileageBelowCurrent,
  isEnteredMileageBelowVehicle,
} from "../../services/mileage";
// Single source of truth: the exact edit fields from LatestServices.
import {
  ServiceFormFields,
  toPersianNumericDisplay,
  toPersianDigits,
  toLatinDigits,
  parsePersianNumber,
  isValidISODate,
} from "../LatestServices/components";

const pad2 = (n) => String(n).padStart(2, "0");

const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

function Periodic() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isCustomerFlow = location.state?.customerServiceRegistration === true || (() => {
    try {
      return sessionStorage.getItem("tasmeh:lastSection") === "customer" ||
        localStorage.getItem("tasmeh:lastSection") === "customer";
    } catch {
      return false;
    }
  })();
  const { getSelectedCar, selectCar, updateMileage } = useCars();

  const car = getSelectedCar();

  const [category, setCategory] = useState(null);
  // Display strings: Persian digits + thousands separator (٬). Parsed to Latin numbers on submit.
  const [mileage, setMileage] = useState("");
  const [nextMileage, setNextMileage] = useState("");
  // Tracks whether the user manually edited Next Service Mileage.
  // While untouched it auto-follows Current Mileage + 10,000;
  // once edited it stays exactly as the user left it.
  const nextMileageTouchedRef = useRef(false);
  const [mechanic, setMechanic] = useState("");
  // Gregorian ISO "YYYY-MM-DD" internally; ServiceDatePicker renders Jalali.
  const [serviceDate, setServiceDate] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [isSelectCarPopOpen, setIsSelectCarPopOpen] = useState(false);
  // Overwrite confirmation: shown only when the entered Current Mileage is
  // strictly lower than the vehicle's stored mileage.
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmPending, setConfirmPending] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const prevCarIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadCategory = async () => {
      try {
        const response = await getCategoryById(categoryId);
        if (!cancelled && response.success && response.data) {
          setCategory(response.data);
        }
      } catch (err) {
        console.error("Failed to load category:", err);
      }
    };
    if (categoryId) loadCategory();
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  // Prefill today's date once.
  useEffect(() => {
    if (!serviceDate) {
      setServiceDate(todayLocalISO());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the mileage fields connected to the selected vehicle:
  // whenever the user picks another vehicle, resync Current Mileage to that
  // vehicle's mileage and (re)initialize Next Service Mileage to
  // Current + 10,000 with the manual-edit flag reset (first load included).
  useEffect(() => {
    if (car && prevCarIdRef.current !== car.id) {
      prevCarIdRef.current = car.id;
      const display = toPersianNumericDisplay(String(car.currentMileage ?? ""));
      setMileage(display);
      nextMileageTouchedRef.current = false;
      setNextMileage(formatDefaultNextMileage(display));
    }
  }, [car]);

  const handleMileageChange = (e) => {
    // Accepts English + Persian/Arabic digits; always displays Persian + separators.
    const display = toPersianNumericDisplay(e.target.value);
    setMileage(display);
    // Auto-follow Current + 10,000 unless the user manually edited Next.
    if (!nextMileageTouchedRef.current) {
      setNextMileage(formatDefaultNextMileage(display));
    }
  };

  const handleOpenSelectCarPop = () => setIsSelectCarPopOpen(true);
  const handleCloseSelectCarPop = () => setIsSelectCarPopOpen(false);
  const handleConfirmCarSelection = (carId) => {
    selectCar(carId);
    handleCloseSelectCarPop();
  };

  // Create the service record before opening the review page. The review page
  // then updates this same record when the final cost is entered.
  const doRegister = async ({ mileageNum, nextMileageNum }) => {
    const details = buildDetailsWithNextServiceMileage({}, nextMileageNum);
    if (mechanic.trim()) details.mechanicName = mechanic.trim();
    const response = await createService({
      vehicleId: car.id,
      categoryId: Number(categoryId),
      mileage: mileageNum,
      serviceDate,
      cost: null,
      description: description || "",
      details,
      type: category?.defaultType || "PERIODIC_KM_TIME",
    });
    if (!response?.success) throw new Error(response?.message || "ثبت سرویس انجام نشد");
    const serviceId = response?.data?.id || response?.data?.service?.id || response?.id;
    if (!serviceId) throw new Error("شناسه سرویس دریافت نشد");
    navigate(`/ServiceReview/${categoryId}`, {
      state: {
        serviceDraft: {
          serviceId,
          vehicleId: car.id,
          categoryId: Number(categoryId),
          mileage: mileageNum,
          nextMileage: nextMileageNum,
          serviceDate,
          mechanic: mechanic.trim(),
          description: description || "",
          type: category?.defaultType || "PERIODIC_KM_TIME",
          categoryTitle: category?.title || "سرویس خودرو",
          vehicle: car,
        },
        customerServiceRegistration: isCustomerFlow,
      },
    });
  };

  // «بله»: register the entered mileage as the vehicle's new current
  // mileage via the existing PATCH /vehicles/:id/mileage endpoint, then
  // continue the service registration flow. Form data is preserved —
  // nothing is cleared before the flow completes.
  const handleConfirmYes = async () => {
    const mileageNum = parsePersianNumber(mileage);
    const nextRaw = toLatinDigits(nextMileage).replace(/\D/g, "");
    const nextMileageNum = nextRaw === "" ? null : parsePersianNumber(nextMileage);
    setConfirmPending(true);
    setConfirmError("");
    try {
      await updateMileage(car.id, mileageNum);
      setIsConfirmOpen(false);
      await doRegister({ mileageNum, nextMileageNum });
    } catch (err) {
      setConfirmError(extractErrorMessage(err, "خطا در ثبت کیلومتر خودرو"));
    } finally {
      setConfirmPending(false);
    }
  };

  // «خیر»: keep the vehicle's existing mileage untouched and continue the
  // service registration with the entered values. Inputs are preserved —
  // the popup simply closes and the flow resumes.
  const handleConfirmNo = async () => {
    if (confirmPending) return;
    const mileageNum = parsePersianNumber(mileage);
    const nextRaw = toLatinDigits(nextMileage).replace(/\D/g, "");
    const nextMileageNum = nextRaw === "" ? null : parsePersianNumber(nextMileage);
    setIsConfirmOpen(false);
    setConfirmError("");
    await doRegister({ mileageNum, nextMileageNum });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!car) {
      setError("خودرویی انتخاب نشده است. لطفاً ابتدا یک خودرو انتخاب کنید");
      return;
    }

    // Validate Persian + English input correctly.
    const mileageNum = parsePersianNumber(mileage);
    if (!Number.isFinite(mileageNum) || mileageNum < 0) {
      setError("لطفاً کیلومتر سرویس را به‌صورت معتبر وارد کنید");
      return;
    }

    if (!serviceDate) {
      setError("لطفاً تاریخ سرویس را انتخاب کنید");
      return;
    }

    if (!isValidISODate(serviceDate)) {
      setError("تاریخ سرویس معتبر نیست");
      return;
    }

    // Next Service Mileage is optional, but when filled it must never be
    // lower than the Current Mileage entered in this form — registration
    // is blocked with a clear validation message.
    let nextMileageNum = null;
    if (toLatinDigits(nextMileage).replace(/\D/g, "") !== "") {
      nextMileageNum = parsePersianNumber(nextMileage);
      if (!Number.isFinite(nextMileageNum) || nextMileageNum < 0) {
        setError("لطفاً کیلومتر سرویس بعدی را به‌صورت معتبر وارد کنید");
        return;
      }
      if (isNextMileageBelowCurrent(mileageNum, nextMileageNum)) {
        setError("کیلومتر سرویس بعدی نمی‌تواند کمتر از کیلومتر فعلی باشد");
        return;
      }
    }

    // Current Mileage vs the vehicle's stored mileage: when the entered
    // value is strictly lower, ask for confirmation instead of silently
    // rejecting. Equal / higher values skip the popup entirely.
    if (isEnteredMileageBelowVehicle(mileageNum, car.currentMileage)) {
      setConfirmError("");
      setIsConfirmOpen(true);
      return;
    }

    await doRegister({ mileageNum, nextMileageNum });
  };

  return (
    <div className="periodic-page" dir="rtl">
      {/* RTL back button: right-side slot with the left-pointing arrow asset
          (toward the previous page). Position, layout, and route unchanged. */}
      <Header
        Text="تکمیل سرویس"
        hideR=""
        onStartClick={() => navigate(isCustomerFlow ? "/SelectService?view=details" : "/ServiceTwo")}
      />

      {/* Vehicle display/selection box at the top — identical to the main
          dashboard (bare CarInfo, same theme/spacing/typography/borders).
          Tapping it opens the vehicle selector for this registration. */}
      {car ? (
        <CarInfo
          Src={car.Src}
          Plate1={car.Plate1}
          Plate2={car.Plate2}
          Plate3={car.Plate3}
          End={car.End}
          Km={car.Km}
          CarModel={car.CarModel}
          ProductionYear={car.ProductionYear}
          CarColor={car.CarColor}
          onSelect={handleOpenSelectCarPop}
          hideSelectionUI
        />
      ) : (
        <Card>
          <Text FontWight="Bold" position="right">
            خودرویی انتخاب نشده است
          </Text>

          <Button onClick={handleOpenSelectCarPop}>
            انتخاب خودرو
          </Button>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="periodic-form">
        <Card>
          <Text FontSize="lg" FontWight="Bold" position="right" className="section-title">
            اطلاعات سرویس
          </Text>

          {/* Shared service fields; next-service mileage is kept optional in
              the data contract but is intentionally not shown in this form. */}
          <ServiceFormFields
            mileage={mileage}
            onMileageChange={handleMileageChange}
            dateISO={serviceDate}
            onDateChange={setServiceDate}
            mechanic={mechanic}
            onMechanicChange={(e) => setMechanic(e.target.value)}
            description={description}
            onDescriptionChange={(e) => setDescription(e.target.value)}
            showCost={false}
            mileageLabel="کیلومتر سرویس"
            mileagePlaceholder={`مثال: ${toPersianDigits("1,234,567").replace(/,/g, "٬")}`}
          />

          {error && <Text color="error" position="center" className="error-message">{error}</Text>}
          <Space size="sm" />
          <Button type="submit" disabled={confirmPending} color="shadow" size="full">بررسی و ادامه</Button>
        </Card>
      </form>

      <MileageConfirmPopup
        isOpen={isConfirmOpen}
        pending={confirmPending}
        error={confirmError}
        onConfirm={handleConfirmYes}
        onCancel={handleConfirmNo}
      />

      <SelectCarPop
        isOpen={isSelectCarPopOpen}
        onClose={handleCloseSelectCarPop}
        onConfirm={handleConfirmCarSelection}
      />
    </div>
  );
}

export default Periodic;
